import {Presenter} from '../../../../../Support/with_presenter';
import {Duration} from '../../../../../Domain/Duration';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {CalendarEvent} from '../../../../../Models/CalendarEvent';
import {format, formatISO} from 'date-fns';
import {Crew} from '../../../../../Models/Crew';
import {EventsInteractor} from '../../../../../Calendar/EventsInteractor';
import {RecurringEventUpdateType} from '../../../../../Models/RecurringEventUpdateType';

export enum EditScheduledEventScreen {
    Edit = 'edit',
    EditRecurring = 'edit-recurring',
    DeleteRecurring = 'delete-recurring',
}

export interface EditScreen {
    screen: EditScheduledEventScreen.Edit;
}

export interface EditRecurringScreen {
    screen: EditScheduledEventScreen.EditRecurring;
    allowedUpdateTypes: RecurringEventUpdateType[];
}

export interface DeleteRecurringScreen {
    screen: EditScheduledEventScreen.DeleteRecurring;
}

export type EditScheduledEventScreenType = EditScreen | EditRecurringScreen | DeleteRecurringScreen;

export class EditScheduledEventPresenter implements Presenter {
    public screen: EditScheduledEventScreenType | null = null;
    public time: string;
    public startAt: Date | null;
    public duration: Duration | null;
    public title: string;
    public description: string;

    public get validates(): boolean {
        return this.title.length > 0 && this.description.length > 0 && this.startAt !== null && this.duration !== null;
    }

    public get timeOptions(): string[] {
        //Returns a list from 00:00 to 23:45 in 15 minute increments
        const timeOptions: string[] = [];
        for (let i = 0; i < 24; i++) {
            for (let j = 0; j < 4; j++) {
                timeOptions.push(`${i.toString().padStart(2, '0')}:${(j * 15).toString().padStart(2, '0')}`);
            }
        }

        return timeOptions;
    }

    constructor(
        updateDateTo: Date | undefined,
        private crew: Crew,
        private calendarEvent: CalendarEvent,
        private onFinish: () => void,
        private eventsInteractor: EventsInteractor,
    ) {
        this.time = format(utcToZonedTime(calendarEvent.startAt, crew.timezone), 'HH:mm');
        this.startAt = updateDateTo ? updateDateTo : utcToZonedTime(calendarEvent.startAt, crew.timezone);
        this.duration = calendarEvent.scheduledEvent.duration;
        this.title = calendarEvent.title;
        this.description = calendarEvent.description;

        if (updateDateTo) {
            if (calendarEvent.scheduledEvent.recurrenceRule) {
                this.screen = {
                    screen: EditScheduledEventScreen.EditRecurring,
                    allowedUpdateTypes: [RecurringEventUpdateType.Single, RecurringEventUpdateType.Future],
                };
            } else {
                this.onSubmit();
            }
        } else {
            this.screen = {
                screen: EditScheduledEventScreen.Edit,
            };
        }
    }

    public mount(): void {
        /* Noop */
    }

    public unmount(): void {
        /* Noop */
    }

    public setTitle(title: string): void {
        this.title = title;
    }

    public setDescription(description: string): void {
        this.description = description;
    }

    public setTime(time: string): void {
        this.time = time;
    }

    public setStartAt(startAt: Date | null): void {
        this.startAt = startAt;
    }

    public setDuration(duration: Duration | null): void {
        this.duration = duration;
    }

    public async onSubmit(): Promise<void> {
        const startAt = zonedTimeToUtc(
            `${this.startAt!.toISOString().split('T')[0]}T${this.time}:00`,
            this.crew.timezone,
        );

        if (!this.calendarEvent.scheduledEvent.recurrenceRule) {
            try {
                await this.eventsInteractor.update(
                    {
                        type: RecurringEventUpdateType.All,
                        id: this.calendarEvent.scheduledEvent.id,
                    },
                    this.title,
                    this.description,
                    startAt!,
                    this.duration!,
                );
                this.onFinish();
            } catch (e) {
                //TODO
            }
        } else {
            //When startAt has been edited, only the current event OR all future events can be updated
            this.screen = {
                screen: EditScheduledEventScreen.EditRecurring,
                allowedUpdateTypes:
                    startAt.getTime() !== this.calendarEvent.startAt.getTime()
                        ? [RecurringEventUpdateType.Single, RecurringEventUpdateType.Future]
                        : [
                              RecurringEventUpdateType.All,
                              RecurringEventUpdateType.Future,
                              RecurringEventUpdateType.Single,
                          ],
            };
        }
    }

    public async onEditRecurring(type: RecurringEventUpdateType): Promise<void> {
        try {
            const startAt = zonedTimeToUtc(
                `${this.startAt!.toISOString().split('T')[0]}T${this.time}:00`,
                this.crew.timezone,
            );

            switch (type) {
                case RecurringEventUpdateType.Single:
                    await this.eventsInteractor.update(
                        {
                            type: RecurringEventUpdateType.Single,
                            id: this.calendarEvent.scheduledEvent.id,
                            date: this.calendarEvent.startAt,
                        },
                        this.title,
                        this.description,
                        startAt!,
                        this.duration!,
                    );
                    this.onFinish();
                    break;
                case RecurringEventUpdateType.All:
                    await this.eventsInteractor.update(
                        {
                            type: RecurringEventUpdateType.All,
                            id: this.calendarEvent.scheduledEvent.id,
                        },
                        this.title,
                        this.description,
                        this.calendarEvent.scheduledEvent.startAt,
                        this.duration!,
                    );
                    this.onFinish();
                    break;
                case RecurringEventUpdateType.Future:
                    await this.eventsInteractor.update(
                        {
                            type: RecurringEventUpdateType.Future,
                            id: this.calendarEvent.scheduledEvent.id,
                            date: this.calendarEvent.startAt,
                        },
                        this.title,
                        this.description,
                        startAt!,
                        this.duration!,
                    );
                    this.onFinish();
                    break;
            }
        } catch (e) {
            //TODO
        }
    }

    public async onDelete(): Promise<void> {
        if (!this.calendarEvent.scheduledEvent.recurrenceRule) {
            try {
                if (confirm('Are you sure you want to delete this event?')) {
                    await this.eventsInteractor.delete({
                        type: RecurringEventUpdateType.All,
                        id: this.calendarEvent.scheduledEvent.id,
                    });
                    this.onFinish();
                }
            } catch (e) {
                //TODO
            }
        } else {
            this.screen = {
                screen: EditScheduledEventScreen.DeleteRecurring,
            };
        }
    }

    public async onDeleteRecurring(type: RecurringEventUpdateType) {
        try {
            switch (type) {
                case RecurringEventUpdateType.Single:
                    await this.eventsInteractor.delete({
                        type: RecurringEventUpdateType.Single,
                        id: this.calendarEvent.scheduledEvent.id,
                        date: this.calendarEvent.startAt,
                    });
                    break;
                case RecurringEventUpdateType.All:
                    await this.eventsInteractor.delete({
                        type: RecurringEventUpdateType.All,
                        id: this.calendarEvent.scheduledEvent.id,
                    });
                    break;
                case RecurringEventUpdateType.Future:
                    await this.eventsInteractor.delete({
                        type: RecurringEventUpdateType.Future,
                        id: this.calendarEvent.scheduledEvent.id,
                        date: this.calendarEvent.startAt,
                    });
                    break;
            }
            this.onFinish();
        } catch (e) {
            //TODO
        }
    }
}
